/* global React, Icon, Card, IconButton, Checkbox, FilterPanel, makeFilter, toast, fmt, PageHeader, Segmented, useState, useMemo, t, PlummeI18n, signedAmountColor, reportPeriodLabel, resolveReportPeriod, reportFilterFor, reportExportLocale, accountHelpProps, SHOW_AS, ReportPeriodNotice, ReportDimensionFilters, TrialBalanceCounterpartyAccount, fmtAs */
// ============================================================
// PLUMME 2.0 - Saldobalanse + Resultatregnskap
// ============================================================

function downloadReportBase64(base64, fileName, mime = 'application/pdf') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function reportWorksheetRows(report) {
  const item = t('rep.export.item');
  const amount = t('rep.export.amount');
  const rows = [[report.title], [t('rep.export.year'), report.year], [t('rep.export.period'), report.periodLabel], []];
  if (report.reportingBasis) rows.splice(3, 0, [t('rep.importedBasisTitle'), t('rep.importedBasisExport')]);
  if (report.summaryRows?.length) {
    rows.push([t('rep.export.summary')], [item, amount]);
    report.summaryRows.forEach((row) => rows.push([row.label, row.amount]));
    rows.push([]);
  }
  (report.sections || []).forEach((section) => {
    const hasTrialColumns = section.rows.some((row) => row.opening != null || row.movement != null || row.closing != null);
    rows.push([section.title], hasTrialColumns ? [item, t('rep.balance.opening'), t('rep.balance.movement'), t('rep.balance.closing')] : [item, amount]);
    section.rows.forEach((row) => {
      rows.push(hasTrialColumns ? [row.label, row.opening || 0, row.movement || 0, row.closing || 0] : [row.label, row.amount]);
    });
    rows.push([]);
  });
  return rows;
}

async function exportReportExcel(report) {
  const filePrefix = report.reportType === 'income-statement' ? 'resultatregnskap' : 'saldobalanse';
  // Uten await ville en uinnlastet XLSX gitt .xls-fallbacken under uten forklaring.
  await loadXLSX().catch(() => {});
  if (window.XLSX) {
    const workbook = window.XLSX.utils.book_new();
    const worksheet = window.XLSX.utils.aoa_to_sheet(reportWorksheetRows(report));
    window.XLSX.utils.book_append_sheet(workbook, worksheet, report.title.slice(0, 31));
    window.XLSX.writeFile(workbook, `${filePrefix}-${report.year}.xlsx`);
    return;
  }
  const rows = reportWorksheetRows(report).map((row, index) => ({ id: index, label: row[0] || '', amount: row[1] || '' }));
  window.exportTable(`${filePrefix}-${report.year}`, [
    { header: t('rep.export.item'), value: (row) => row.label },
    { header: t('rep.export.amount'), value: (row) => row.amount },
  ], rows, 'xls');
}

function ExportMenu({ report }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const pick = async (kind) => {
    setOpen(false);
    if (!report) return;
    setBusy(kind);
    try {
      if (kind === 'PDF') {
        if (!window.PlummeBackend?.downloadReportPdf) throw new Error(t('rep.export.pdfUnavailable'));
        const result = await window.PlummeBackend.downloadReportPdf(report);
        downloadReportBase64(result.pdfBase64, result.fileName);
      } else {
        await exportReportExcel(report);
      }
      toast(t('rep.export.downloaded', { format: kind }), 'success');
    } catch (error) {
      toast(error?.message || t('rep.export.failed'), 'error');
    } finally {
      setBusy('');
    }
  };
  return <div className="popwrap">
    <IconButton name="download" data-help={t('helpTopics.exportFormat.body')} data-help-title={t('helpTopics.exportFormat.title')} onClick={() => setOpen((o) => !o)} aria-label={t('list.download')} title={busy ? t('rep.export.busy') : t('list.download')} disabled={!!busy || !report} />
    {open && <React.Fragment>
      <div className="pop-scrim" onClick={() => setOpen(false)} />
      <div className="pop pop-menu right">
        <button onClick={() => pick('PDF')}><Icon name="fileText" size={18} />{t('po.pdf')}</button>
        <button onClick={() => pick('Excel')}><Icon name="files" size={18} />{t('rep.excel')}</button>
      </div>
    </React.Fragment>}
  </div>;
}

function ReportHead({ title, subtitle, action, card = true, titleHelp }) {
  return <PageHeader className="rep-head" title={title} subtitle={subtitle} card={card} help titleHelp={titleHelp}>
    {action}
  </PageHeader>;
}

function ImportedReportingBasisNotice({ basis }) {
  if (basis?.kind !== 'imported_accounting_statistics' || basis?.validated !== true) return null;
  return <div className="notice warning mt-12" role="status"><b>{t('rep.importedBasisTitle')}</b> {t('rep.importedBasisText')}</div>;
}

function trialBalanceExportPayload(year, filter, trialBalance, valMode = 'ut', colLabel = t('rep.balance.closing').toLocaleUpperCase(), reportingBasis = null) {
  return {
    reportType: 'trial-balance',
    locale: reportExportLocale(),
    year,
    periodLabel: reportPeriodLabel(year, filter),
    title: t('rep.trial.exportTitle', { column: colLabel }),
    reportingBasis,
    summaryRows: [],
    sections: trialBalance.map((group) => ({
      title: trialGroupLabel(group),
      rows: visibleTrialAccounts(group.accounts, !!filter.checks?.bevegelse, !filter.checks?.inaktive).map((account) => ({
        label: `${account.nr} ${account.name}`,
        opening: account.inn || 0,
        movement: trialAccountChange(account),
        closing: account.ut || 0,
        amount: valMode === 'inn' ? (account.inn || 0) : valMode === 'endring' ? trialAccountChange(account) : (account.ut || 0),
      })),
    })),
  };
}

// The engine sends a translation key for each trial balance group; older models may still carry a label.
function trialGroupLabel(group) {
  return group?.labelKey ? t(group.labelKey) : String(group?.label || group?.key || '');
}

// Under denne grensen regnes en kontoverdi som null (avrundingsstøy fra bokføringen).
const TRIAL_ZERO = 0.005;
const trialAccountChange = (account) => account.endring ?? ((account.ut || 0) - (account.inn || 0));
const trialAccountHasMovement = (account) => Math.abs(account.endring ?? account.ut) > TRIAL_ZERO;
// "Aktiv" er løsere enn "bevegelse": en konto med inngående saldo, men uten bevegelse i
// perioden, har fortsatt en faktisk verdi og skal bli stående.
const trialAccountIsActive = (account) => Math.abs(account.inn || 0) > TRIAL_ZERO
  || Math.abs(trialAccountChange(account)) > TRIAL_ZERO
  || Math.abs(account.ut || 0) > TRIAL_ZERO;
function visibleTrialAccounts(accounts, onlyMovement, onlyActive) {
  let list = accounts;
  if (onlyMovement) list = list.filter(trialAccountHasMovement);
  if (onlyActive) list = list.filter(trialAccountIsActive);
  return list;
}
function SaldobalansePage({ year, setRoute, setYear }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [filter, setFilter] = useState(() => makeFilter(window.PlummeTrialBalancePresentation.consumeReportFilter('saldobalanse') || {}));
  const [collapsed, setCollapsed] = useState({});
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [valMode, setValMode] = useState('ut');
  const [mode, setMode] = useState('kroner');
  const onlyMovement = !!filter.checks.bevegelse;
  const onlyActive = !filter.checks.inaktive; // Standard er bare aktive konti; brukeren huker av for resten av kontoplanen.
  // Checkboksene bor i filter.checks slik at «Nullstill filter» og aktiv-prikken på knappen
  // fortsatt ser dem. «Vis som» er ren presentasjon og hører ikke i filteret.
  const setCheck = (key) => (on) => setFilter({ ...filter, checks: { ...filter.checks, [key]: on } });
  // The report year follows the selected period; `year` stays the app's year and anchors the picker.
  const period = useMemo(() => resolveReportPeriod(year, filter), [filter, year]);
  const reportYear = period.year;
  const reportFilter = useMemo(() => reportFilterFor(filter, period), [filter, period]);
  const model = useMemo(() => {
    if (period.crossYear || !window.PlummeBackend?.getReportsModel) return null;
    return window.PlummeBackend.getReportsModel({
      year: reportYear,
      from: period.from,
      to: period.to,
      months: period.months,
      projectId: filter.projectId || '',
      departmentId: filter.departmentId || '',
    });
  }, [backend?.appState, backend?.activeBusinessId, period, reportYear]);
  const trialBalance = model?.trial || [];
  const accountingCountry = String(backend?.appState?.settings?.country || '').toUpperCase();
  const payrollVouchers = useMemo(() => {
    if (accountingCountry !== 'NO' || !window.PlummeBackend?.getVouchersModel) return [];
    return window.PlummeBackend.getVouchersModel({ year: reportYear });
  }, [accountingCountry, backend?.appState, backend?.activeBusinessId, reportYear]);
  const investmentVouchers = useMemo(() => accountingCountry === 'NO' && window.PlummeBackend?.getVouchersModel ? window.PlummeBackend.getVouchersModel() : [], [accountingCountry, backend?.appState, backend?.activeBusinessId]);
  const investmentInstruments = backend?.appState?.investmentRegister?.instruments || [];
  const valOf = (account) => valMode === 'inn' ? (account.inn || 0) : valMode === 'endring' ? (account.endring ?? ((account.ut || 0) - (account.inn || 0))) : account.ut;
  const colLabel = t(valMode === 'inn' ? 'rep.balance.opening' : valMode === 'endring' ? 'rep.balance.movement' : 'rep.balance.closing').toLocaleUpperCase();
  const exportPayload = useMemo(() => period.crossYear ? null : trialBalanceExportPayload(reportYear, reportFilter, trialBalance, valMode, colLabel, model?.reportingBasis), [period, reportFilter, trialBalance, reportYear, valMode, colLabel, model?.reportingBasis]);
  const hasVisibleRows = trialBalance.some((grp) => visibleTrialAccounts(grp.accounts, onlyMovement, onlyActive).length > 0);
  const openAccount = (account) => {
    window.PlummeTrialBalancePresentation.rememberReportFilter('saldobalanse', filter);
    window.__bilagAccount = { nr: account.nr, name: account.name, from: 'saldobalanse', filter: { from: filter.from, to: filter.to, months: filter.months } };
    if (account.includeSubaccounts) window.__bilagAccount.includeSubaccounts = true;
    if (Array.isArray(account.voucherIds)) window.__bilagAccount.voucherIds = account.voucherIds.slice();
    if (account.employeeId) window.__bilagAccount.employeeId = account.employeeId;
    // The voucher list is scoped to the app's year, so follow the report into its year.
    if (setYear && reportYear !== year) setYear(reportYear);
    setRoute && setRoute('allebilag');
  }; useReportPageControlAdapter({ page: 'trial_balance', year: reportYear, filter, setFilter, view: valMode, setView: setValMode, views: ['inn', 'endring', 'ut'], blocked: model?.blocked === true, accountingBasis: model?.accountingBasis, reportingBasis: model?.reportingBasis, rows: () => trialBalance.flatMap((group) => group.accounts || []), summary: () => ({ accountCount: trialBalance.reduce((sum, group) => sum + (group.accounts || []).length, 0) }), openAccount });
  const accountRow = (account, options = {}) => { const value = valOf(account); const selectedAccount = options.name ? { ...account, name: options.name } : account; return <div data-app-control="true" key={options.key || account.nr} className={'acct-row acct-clickable'+(options.child ? ' acct-child' : '')} style={options.depth === 2 ? { paddingLeft: 32 } : undefined} {...accountHelpProps(account.nr, options.name || account.name)} onClick={() => openAccount(selectedAccount)} title={t('rep.showVouchersFor', { account: `${account.nr} ${options.name || account.name}` })}>
    <span className="acct-nr">{account.nr}</span>
    <span className="acct-name">{options.name || account.name}</span>
    <span className="acct-val" style={{ color: signedAmountColor(value) }}>{fmtAs(value, mode)}</span>
  </div>; };
  if (model?.blocked) return model.loading ? <window.BrandLoading text={t('rep.journalLoading')} compact /> : <Card><EmptyState acc="none" mood="concerned" title={t('rep.incompleteBasisTitle')} text={t('rep.incompleteBasisText')} compact /></Card>;
  return <div>
    <Card>
      <ReportHead title={t('nav.trialBalance')} subtitle={t('rep.allAccountsSub')} titleHelp={t('rep.trial.titleHelp')} action={<React.Fragment><FilterPanel value={filter} onChange={setFilter} onReset={() => setFilter(makeFilter())} dates period year={year}>
        <ReportDimensionFilters filter={filter} setFilter={setFilter} dimensions={model?.dimensions} />
        <div className="filter-group" data-help-title={t('vat.view')} data-help={t('rep.trial.viewHelp')}><div className="filter-group-label">{t('vat.view')}</div>
          <div className="col gap-12" style={{ marginTop: 4 }}>
            <Checkbox checked={onlyMovement} onChange={setCheck('bevegelse')}>{t('rep.trial.onlyMovement')}</Checkbox>
            <Checkbox checked={!onlyActive} onChange={setCheck('inaktive')}>{t('rep.trial.showInactive')}</Checkbox>
            <div className="field"><label>{t('rep.showAs')}</label><Select value={mode} onChange={(event) => setMode(event.target.value)}>{SHOW_AS.map((option) => <option key={option.value} value={option.value}>{t(option.labelKey)}</option>)}</Select></div>
          </div>
        </div>
      </FilterPanel><ExportMenu report={exportPayload} /></React.Fragment>} card={false} />
      <ReportPeriodNotice period={period} appYear={year} />
      <ImportedReportingBasisNotice basis={model?.reportingBasis} />
    </Card>

    <div className="row sb-modes" style={{ marginTop: 16 }} data-help-title={t('rep.openingChangeClosing')} data-help={t('rep.balanceModeHelp')}>
      <Segmented soft options={[{ value: 'inn', label: t('rep.balance.opening') }, { value: 'endring', label: t('rep.balance.movement') }, { value: 'ut', label: t('rep.balance.closing') }]} value={valMode} onChange={setValMode} />
    </div>

    <Card className="mt-16">
      {trialBalance.map((grp) => {
        const accs = visibleTrialAccounts(grp.accounts, onlyMovement, onlyActive);
        const displayRows = window.PlummeTrialBalancePresentation.buildTrialBalanceRows(accs, {
          country: accountingCountry,
          vouchers: payrollVouchers,
          investmentVouchers,
          instruments: investmentInstruments,
          state: backend?.appState || {},
          year: reportYear,
          filter: reportFilter,
          labels: {
            familyName: (code) => code === '2941' ? t('rep.trial.holidayPayLiability') : t('rep.trial.payrollLiability'),
            payrollRun: (period, number) => t('rep.trial.payrollRun', { period: period || number }),
            term: (term, reportingYear) => t('rep.trial.term', { term, year: reportingYear }),
            accrualYear: (accrualYear) => t('rep.trial.accrualYear', { year: accrualYear }),
          },
        });
        if (!accs.length) return null;
        const sum = grp.accounts.reduce((s, account) => s + valOf(account), 0);
        const open = !collapsed[grp.key];
        const groupLabel = trialGroupLabel(grp);
        return <div key={grp.key}>
          <div data-app-control="true" className="grp-head" style={{ cursor: 'pointer' }} onClick={() => setCollapsed((current) => ({ ...current, [grp.key]: !current[grp.key] }))} data-help-title={groupLabel} data-help={t('rep.groupsHelp')}>
            <span className="row gap-8"><Icon name={open ? 'chevDown' : 'chevRight'} size={16} className="muted" /><span className="grp-label">{groupLabel}</span></span>
            <span className="grp-label" style={{ color: 'var(--muted)' }}>{colLabel}</span>
          </div>
          {open && displayRows.map((row) => {
            if (row.kind === 'account' && accountingCountry === 'NO' && ['1500', '2400'].includes(String(row.account.nr))) return <TrialBalanceCounterpartyAccount key={row.account.nr} kind={String(row.account.nr) === '1500' ? 'customer' : 'supplier'} account={row.account} value={valOf(row.account)} displayMode={mode} balanceMode={valMode} period={period} state={backend?.appState || {}} projectId={filter.projectId || ''} departmentId={filter.departmentId || ''} onOpenAccount={openAccount} setRoute={setRoute} />;
            if (row.kind === 'account') return accountRow(row.account);
            const expanded = !!expandedAccounts[row.key];
            const value = valOf(row.account);
            const childCount = row.childAccounts.length;
            const employeeCountKey = childCount === 1 ? 'rep.trial.oneEmployee' : 'rep.trial.employeeCount';
            return <React.Fragment key={row.key}>
              <button type="button" className="acct-row acct-clickable acct-payroll-toggle" aria-expanded={expanded} onClick={() => setExpandedAccounts((current) => ({ ...current, [row.key]: !current[row.key] }))}>
                <span className="acct-nr row gap-8"><Icon name={expanded ? 'chevDown' : 'chevRight'} size={15} /><span>{row.account.nr}</span></span>
                <span className="acct-name"><span>{row.account.name}</span>{row.kind === 'payroll-family' && <span className="acct-count">{t(employeeCountKey, { n: childCount })}</span>}</span>
                <span className="acct-val" style={{ color: signedAmountColor(value) }}>{fmtAs(value, mode)}</span>
              </button>
              {expanded && accountRow({ ...row.account, includeSubaccounts: row.kind === 'payroll-family' }, { key: `${row.key}-all`, child: true, name: t('rep.trial.allAccountEntries', { account: row.account.nr }) })}
              {expanded && row.childAccounts.map((account, index) => <React.Fragment key={`${row.key}-child-${index}`}>
                {accountRow(account, { key: `${row.key}-child-${index}`, child: true })}
                {(account.children || []).map((child, childIndex) => accountRow(child, { key: `${row.key}-child-${index}-${childIndex}`, child: true, depth: 2 }))}
              </React.Fragment>)}
            </React.Fragment>;
          })}
          {open && <div className="acct-row" style={{ borderTop: '1.5px solid var(--border-strong)', borderBottom: 'none' }}>
            <span className="acct-nr" />
            <span className="acct-name" style={{ color: 'var(--ink)', fontWeight: 900 }}>{t('rep.groupTotal', { group: groupLabel.toLocaleLowerCase() })}</span>
            <span className="acct-val" style={{ color: signedAmountColor(sum) }}>{fmtAs(sum, mode)}</span>
          </div>}
        </div>;
      })}
      {!hasVisibleRows && !period.crossYear && <div className="muted" style={{ fontWeight: 700, padding: '18px 2px' }}>
        {t('rep.trial.empty')}
      </div>}
    </Card>
  </div>;
}

// The engine returns translation keys (`labelKeys`); these are the fallback when no model exists.
const RESULT_LABEL_KEYS = {
  title: 'nav.incomeStatement',
  subtitle: 'rep.result.subtitle',
  income: 'rep.result.income',
  costs: 'rep.result.costs',
  finance: 'rep.result.finance',
  result: 'rep.result.beforeTax',
  noLines: 'rep.result.noLines',
  total: 'rep.total',
  showDetails: 'rep.showDetails',
  hideDetails: 'rep.hideDetails',
};
const EMPTY_RESULT_MODEL = {
  labelKeys: RESULT_LABEL_KEYS,
  inntekter: 0,
  kostnader: 0,
  finans: 0,
  resultat: 0,
  sections: [
    { key: 'inntekter', labelKey: RESULT_LABEL_KEYS.income, tone: 'green', total: 0, lines: [] },
    { key: 'kostnader', labelKey: RESULT_LABEL_KEYS.costs, tone: 'peach', total: 0, lines: [] },
    { key: 'finans', labelKey: RESULT_LABEL_KEYS.finance, tone: 'plum', total: 0, lines: [] },
  ],
};
function resultLabels(model) {
  const keys = { ...RESULT_LABEL_KEYS, ...(model?.labelKeys || {}) };
  return Object.fromEntries(Object.entries(keys).map(([name, key]) => [name, t(key)]));
}
const resultSectionLabel = (section) => (section?.labelKey ? t(section.labelKey) : String(section?.label || section?.key || ''));
function buildIncomeStatementExport({ year, filter, model, reportingBasis = null }) {
  const labels = resultLabels(model);
  return {
    reportType: 'income-statement',
    locale: reportExportLocale(),
    year,
    periodLabel: reportPeriodLabel(year, filter),
    title: labels.title,
    reportingBasis,
    summaryRows: [
      { label: labels.income, amount: model.inntekter || 0 },
      { label: labels.costs, amount: model.kostnader || 0 },
      { label: labels.finance, amount: model.finans || 0 },
      { label: labels.result, amount: model.resultat || 0 },
    ],
    sections: (model.sections || []).map((section) => ({
      title: resultSectionLabel(section),
      rows: (section.lines || []).filter(line => Math.abs(line.amount) > 0.005).map((line) => ({ label: `${line.nr} ${line.name}`, amount: line.amount })),
    })),
  };
}
function ResultatregnskapPage({ year, setRoute, setYear }) {
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const [filter, setFilter] = useState(makeFilter());
  const [showKonto, setShowKonto] = useState(true), [showZero, setShowZero] = useState(false);
  const [mode, setMode] = useState('kroner');
  const [open, setOpen] = useState({ inntekter: true, kostnader: true, finans: false });
  const tone = { green: 'var(--success)', peach: 'var(--warning)', plum: 'var(--plum-700)' };
  // The report year follows the selected period; `year` stays the app's year and anchors the picker.
  const period = useMemo(() => resolveReportPeriod(year, filter), [filter, year]);
  const reportYear = period.year;
  const reportsModel = useMemo(() => period.crossYear ? null : window.PlummeBackend?.getReportsModel?.({ year: period.year, from: period.from, to: period.to, months: period.months, projectId: filter.projectId || '', departmentId: filter.departmentId || '' }), [backend?.appState, backend?.activeBusinessId, period, filter.projectId, filter.departmentId]);
  const resultModel = reportsModel?.result || EMPTY_RESULT_MODEL;
  const labels = resultLabels(resultModel);
  const exportPayload = useMemo(() => period.crossYear ? null : buildIncomeStatementExport({ year: reportYear, filter: reportFilterFor(filter, period), model: resultModel, reportingBasis: reportsModel?.reportingBasis }), [filter, period, resultModel, reportYear, reportsModel?.reportingBasis]);
  const openAccountVouchers = (line) => {
    window.__bilagAccount = {
      nr: line.nr,
      name: line.name,
      from: 'resultatregnskap',
      filter: { from: filter.from, to: filter.to, months: filter.months },
    };
    // The voucher list is scoped to the app's year, so follow the report into its year.
    if (setYear && reportYear !== year) setYear(reportYear);
    setRoute && setRoute('allebilag');
  }; useReportPageControlAdapter({ page: 'income_statement', year: reportYear, filter, setFilter, view: mode, setView: setMode, views: SHOW_AS.map((option) => option.value), blocked: reportsModel?.blocked === true, accountingBasis: reportsModel?.accountingBasis, reportingBasis: reportsModel?.reportingBasis, rows: () => (resultModel.sections || []).flatMap((section) => section.lines || []), summary: () => ({ income: resultModel.inntekter || 0, costs: resultModel.kostnader || 0, finance: resultModel.finans || 0, result: resultModel.resultat || 0 }), openAccount: openAccountVouchers });
  if (reportsModel?.blocked) return reportsModel.loading ? <window.BrandLoading text={t('rep.journalLoading')} compact /> : <Card><EmptyState acc="none" mood="concerned" title={t('rep.incompleteBasisTitle')} text={t('rep.incompleteBasisText')} compact /></Card>;
  return <div>
    <Card>
      <ReportHead title={labels.title} subtitle={labels.subtitle} titleHelp={t('rep.result.titleHelp')} action={<React.Fragment><FilterPanel value={filter} onChange={setFilter} onReset={() => setFilter(makeFilter())} dates period year={year}>
        <ReportDimensionFilters filter={filter} setFilter={setFilter} dimensions={reportsModel?.dimensions} />
        <div className="filter-group" data-help-title={t('vat.view')} data-help={t('rep.displayHelp')}><div className="filter-group-label">{t('vat.view')}</div>
          <div className="col gap-12" style={{ marginTop: 4 }}>
            <Checkbox checked={showKonto} onChange={setShowKonto}>{t('rep.showAccount')}</Checkbox><Checkbox checked={showZero} onChange={setShowZero}>{t('rep.showZero')}</Checkbox>
            <div className="field"><label>{t('rep.showAs')}</label><Select value={mode} onChange={(event) => setMode(event.target.value)}>{SHOW_AS.map((option) => <option key={option.value} value={option.value}>{t(option.labelKey)}</option>)}</Select></div>
          </div>
        </div>
      </FilterPanel><ExportMenu report={exportPayload} /></React.Fragment>} card={false} />
      <ReportPeriodNotice period={period} appYear={year} />
      <ImportedReportingBasisNotice basis={reportsModel?.reportingBasis} />
    </Card>

    <div className="res-tiles mt-20">
      <div className="res-tile green" data-help-title={labels.income} data-help={t('rep.operatingIncomeHelp')}><span className="res-lbl">{labels.income}</span><span className="res-val">{fmtAs(resultModel.inntekter, mode)}</span></div>
      <div className="res-tile peach" data-help-title={labels.costs} data-help={t('rep.operatingCostHelp')}><span className="res-lbl">{labels.costs}</span><span className="res-val">{fmtAs(resultModel.kostnader, mode)}</span></div>
    </div>
    <div className="res-bar mt-16" data-help-title={labels.result} data-help={t('rep.resultHelp')}>
      <span className="rb-lbl">{labels.result}</span>
      <span className="rb-val">{fmtAs(resultModel.resultat, mode)}</span>
    </div>

    <div className="mt-20">
      {(() => {
        const secMax = Math.max(1, ...(resultModel.sections || []).map((section) => Math.abs(section.total)));
        return (resultModel.sections || []).map((sec) => {
          const isOpen = open[sec.key], visibleLines = (sec.lines || []).filter(line => showZero || Math.abs(line.amount) > 0.005);
          const sectionLabel = resultSectionLabel(sec);
          const barPct = Math.round((Math.abs(sec.total) / secMax) * 100);
          return <div key={sec.key} className="res-section">
            <div data-app-control="true" className="res-sec-head" onClick={() => setOpen((current) => ({ ...current, [sec.key]: !current[sec.key] }))} data-help-title={sectionLabel} data-help={t('rep.groupHelp')}>
              <Icon name={isOpen ? 'chevDown' : 'chevRight'} size={18} className="muted" style={{ marginTop: 3 }} />
              <div className="grow">
                <div className="row between"><b className="t-h3 res-sec-title">{sectionLabel}</b><b className="t-num res-sec-total">{fmtAs(sec.total, mode)}</b></div>
                <div className="t-sm muted" style={{ fontWeight: 700, marginTop: 2 }}>{isOpen ? labels.hideDetails : labels.showDetails}</div>
                <div className="bar mt-8" title={t('rep.shareOfLargest', { pct: barPct })}><span style={{ width: barPct + '%', background: tone[sec.tone] }} /></div>
              </div>
            </div>
            {isOpen && visibleLines.map((line) => <div data-app-control="true" key={line.nr} className="res-line acct-clickable" {...accountHelpProps(line.nr, line.name)} onClick={() => openAccountVouchers(line)} title={t('rep.showVouchersFor', { account: `${line.nr} ${line.name}` })}>
              <span className="grow row gap-10" style={{ minWidth: 0 }}>
                {showKonto && <span className="acct-nr" style={{ width: 'auto' }}>{line.nr}</span>}
                <span className="res-line-name">{line.name}</span>
              </span>
              <span className="rl-pct">{line.pct} %</span>
              <b className="t-num res-line-amt">{fmtAs(line.amount, mode)}</b>
            </div>)}
            {isOpen && (visibleLines.length ? <div className="res-line sum">
              <span className="grow">{labels.total}</span><b className="t-num">{fmtAs(sec.total, mode)}</b>
            </div> : <div className="res-line"><span className="muted" style={{ fontWeight: 600 }}>{(sec.lines || []).length ? t('rep.zeroHidden') : labels.noLines}</span></div>)}
          </div>;
        });
      })()}
    </div>
  </div>;
}

Object.assign(window, { SaldobalansePage, ResultatregnskapPage });
